import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Check, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAccount } from '../account/store.ts';
import { deleteAccountNow } from '../app/accountActions.ts';
import { config } from '../config.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { IconButton } from '../ui/IconButton.tsx';

const CONFIRM = 'DELETE';

/**
 * Public account-deletion page (Google Play requires a URL that works without the app;
 * docs/research/05 §7). Signed in: delete here (DELETE /v1/me). Otherwise: sign in, or email support.
 */
export function DeleteAccountScreen() {
  const navigate = useNavigate();
  const { session, email, ready } = useAccount(
    useShallow((s) => ({ session: s.session, email: s.me?.user.email ?? s.session?.email ?? null, ready: s.ready })),
  );
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<null | { storeSubscriptionsActive: boolean }>(null);

  useEffect(() => {
    document.title = 'Delete your account – Better Lights';
    return () => {
      document.title = 'Better Lights';
    };
  }, []);

  const mail = `mailto:${config.supportEmail}?subject=${encodeURIComponent('Delete my Better Lights account')}`;

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center gap-2 px-2">
        <IconButton
          icon={ArrowLeft}
          label="Back"
          onClick={() => (history.length > 1 ? history.back() : void navigate({ to: '/' }))}
        />
        <h1 className="t-title">Delete your Better Lights account</h1>
      </div>
      <div className="mx-auto flex max-w-[680px] flex-col gap-6 p-4">
        <section className="flex flex-col gap-2" aria-labelledby="del-what">
          <h2 id="del-what" className="t-heading">
            What gets deleted
          </h2>
          <ul className="t-body flex list-disc flex-col gap-1 pl-5 text-dim">
            <li>Your account, sign-in methods and signed-in devices.</li>
            <li>Projects, photos and AI renders in your personal cloud workspace.</li>
            <li>Your quote branding and price list, and your memberships in company workspaces.</li>
            <li>Web (Stripe) subscriptions are canceled. Unused AI credits are forfeited.</li>
            <li>If you signed in with Apple, the app’s access to your Apple ID is revoked.</li>
          </ul>
        </section>
        <section className="flex flex-col gap-2" aria-labelledby="del-kept">
          <h2 id="del-kept" className="t-heading">
            What is kept
          </h2>
          <ul className="t-body flex list-disc flex-col gap-1 pl-5 text-dim">
            <li>Invoices and payment records, as tax law requires.</li>
            <li>
              AI credit and render usage records, with your identity removed, for fraud prevention and accounting.
            </li>
            <li>Projects in a company workspace: they belong to the company.</li>
            <li>Projects saved only on a device stay there until you delete them or the app.</li>
            <li>
              App Store and Google Play subscriptions continue until you cancel them in the store; deleting the account
              doesn’t cancel them.
            </li>
          </ul>
          <p className="t-caption text-muted">
            If you’re the only owner of a company workspace with other members or an active Team plan, transfer
            ownership or cancel the plan first. In the apps you can also delete your account under Account › Delete
            Account. Details are in the{' '}
            <Link to="/privacy" className="text-accent underline">
              Privacy Policy
            </Link>
            .
          </p>
        </section>

        {done ? (
          <div role="status" className="flex flex-col gap-2 rounded-md bg-surface p-4">
            <span className="t-label flex items-center gap-2">
              <Check size={18} className="text-success" aria-hidden /> Your account was deleted.
            </span>
            {done.storeSubscriptionsActive ? (
              <span className="t-caption text-dim">
                You still have an App Store or Google Play subscription. Cancel it in your device’s subscription
                settings so you aren’t charged again.
              </span>
            ) : null}
          </div>
        ) : !ready ? (
          <p className="t-body text-dim">Loading…</p>
        ) : session ? (
          <form
            className="flex flex-col gap-3 rounded-md border border-[rgba(255,90,90,0.4)] bg-surface p-4"
            aria-label="Delete account"
            onSubmit={async (e) => {
              e.preventDefault();
              if (typed.trim().toUpperCase() !== CONFIRM) return;
              setBusy(true);
              setError(null);
              const res = await deleteAccountNow();
              setBusy(false);
              if (res.ok) setDone({ storeSubscriptionsActive: res.storeSubscriptionsActive });
              else setError(res.error);
            }}
          >
            <p className="t-body">
              Signed in as <strong>{email}</strong>. This can’t be undone.
            </p>
            <Field
              label={`Type ${CONFIRM} to confirm`}
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
            {error ? (
              <p role="alert" className="t-caption text-danger">
                {error}
              </p>
            ) : null}
            <Button
              type="submit"
              variant="danger"
              icon={Trash2}
              loading={busy}
              disabled={typed.trim().toUpperCase() !== CONFIRM}
              className="self-start"
            >
              Delete my account
            </Button>
          </form>
        ) : (
          <div className="flex flex-col gap-3 rounded-md bg-surface p-4">
            <p className="t-body">Sign in with the account you want to delete, then come back to this page.</p>
            <Button
              variant="primary"
              className="self-start"
              onClick={() => void navigate({ to: '/signin', search: { next: '/delete-account' } })}
            >
              Sign in to delete
            </Button>
            <p className="t-caption text-dim">
              Can’t sign in? Email{' '}
              <a href={mail} className="text-accent underline">
                {config.supportEmail}
              </a>{' '}
              from the address on your account and we’ll delete it within 30 days.
            </p>
          </div>
        )}
        <nav className="t-caption flex flex-wrap gap-4 border-t border-border pt-4 text-muted" aria-label="Legal">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
        </nav>
      </div>
    </main>
  );
}
